import React, { useEffect, useState, useCallback } from 'react';
import { Volume2, Save, RotateCcw, ShieldAlert, ShoppingCart, DollarSign, MessageSquare, Play } from 'lucide-react';
import { SOUND_PRESETS, PRESET_BY_ID, DEFAULT_SOUND_FOR } from '../utils/notifySounds';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENTS = [
  { id: 'login_failed',   label: 'Failed login alert',  sublabel: 'Plays when an admin/user login attempt fails (wrong password, locked account, etc.)', Icon: ShieldAlert,    accent: 'rose'    },
  { id: 'login_brute',    label: 'Brute-force alarm',   sublabel: 'Loud wailing alarm when 5+ failed logins occur within 60 seconds — possible attack in progress', Icon: ShieldAlert, accent: 'rose'    },
  { id: 'order_new',      label: 'New order created',   sublabel: 'Plays the moment a customer submits a new tuning order', Icon: ShoppingCart,  accent: 'emerald' },
  { id: 'recharge_new',   label: 'Account recharge',    sublabel: 'Plays whenever a customer tops up their balance via PayPal, Stripe, or manual', Icon: DollarSign,     accent: 'amber'   },
  { id: 'ticket_message', label: 'New ticket message',  sublabel: 'Plays when a customer sends you a message in a support/order ticket', Icon: MessageSquare, accent: 'violet'  },
];

/**
 * SoundsEditor — admin-wide notification sound preferences.
 * Settings are stored server-side under app_config.notification_sounds_settings,
 * so any admin device picks up the chosen presets immediately on next focus.
 */
export default function SoundsEditor({ token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState({ ...DEFAULT_SOUND_FOR });
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/config/sounds`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setEvents({ ...DEFAULT_SOUND_FOR, ...(j.events || {}) });
    } catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/config/sounds`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setEvents({ ...DEFAULT_SOUND_FOR, ...(j.events || {}) });
      alert('Sound preferences saved.');
    } catch (e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const restoreDefaults = () => {
    if (!window.confirm('Restore default presets for all events?')) return;
    setEvents({ ...DEFAULT_SOUND_FOR });
  };

  const playPreview = (id) => {
    const p = PRESET_BY_ID[id];
    if (p && p.id !== 'silence') { try { p.play(); } catch (_) { /* noop */ } }
  };

  if (loading) return <div className="p-10 text-center text-slate-500" data-testid="sounds-loading">Loading sound preferences…</div>;

  return (
    <div className="space-y-5" data-testid="sounds-editor">
      <div className="bg-gradient-to-r from-cyan-50 to-violet-50 border border-cyan-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center flex-shrink-0">
            <Volume2 className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-bold text-slate-800">Notification sounds</h2>
            <p className="text-sm text-slate-600">
              Pick which short tone plays for each admin notification. Settings apply to every admin browser tab. Use the
              <strong> Test </strong>button to preview before saving. The master <strong>🔔 bell</strong> in the bottom-right still mutes everything.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm" data-testid="sounds-error">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100">
        {EVENTS.map((ev) => {
          const accents = {
            rose:    'bg-rose-100 text-rose-700',
            emerald: 'bg-emerald-100 text-emerald-700',
            amber:   'bg-amber-100 text-amber-700',
            violet:  'bg-violet-100 text-violet-700',
          };
          const value = events[ev.id] || DEFAULT_SOUND_FOR[ev.id];
          return (
            <div key={ev.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`sounds-row-${ev.id}`}>
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${accents[ev.accent]}`}>
                  <ev.Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-800 text-sm">{ev.label}</p>
                  <p className="text-xs text-slate-500">{ev.sublabel}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <select
                  value={value}
                  onChange={(e) => setEvents((p) => ({ ...p, [ev.id]: e.target.value }))}
                  data-testid={`sounds-select-${ev.id}`}
                  className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white min-w-[230px]"
                >
                  {SOUND_PRESETS.map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => playPreview(value)}
                  data-testid={`sounds-test-${ev.id}`}
                  title="Preview sound"
                  className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 transition-colors flex items-center gap-1 text-xs font-bold uppercase tracking-wider"
                >
                  <Play className="w-3.5 h-3.5" /> Test
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={restoreDefaults}
          data-testid="sounds-restore"
          className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-bold text-sm flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" /> Restore defaults
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="sounds-save"
          className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-bold hover:shadow-lg disabled:opacity-50 transition-all flex items-center gap-2"
        >
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save preferences'}
        </button>
      </div>
    </div>
  );
}
